import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  User,
  UserCredential,
} from 'firebase/auth';
import { auth, getSamlProvider, isFirebaseInitialized } from '../config/firebase';

/** Word task pane / Office WebView blocks `signInWithPopup`; use full-page redirect instead. */
function useMicrosoftSamlRedirectFlow(): boolean {
  try {
    return typeof Office !== 'undefined' && typeof Office.context !== 'undefined';
  } catch {
    return false;
  }
}

export interface FirebaseAuthResult {
  success: boolean;
  user?: User;
  error?: string;
  idToken?: string;
  accessToken?: string;
  /** SAML sign-in started via `signInWithRedirect` (task pane will navigate away). */
  redirectInitiated?: boolean;
}

export interface FirebaseUserInfo {
  email: string;
  name: string;
  displayName: string;
  uid: string;
  emailVerified: boolean;
}

/**
 * After returning from `signInWithRedirect`, call once on the login page (dashboard pattern).
 */
export const handleMicrosoftRedirect = async (): Promise<FirebaseAuthResult> => {
  if (!isFirebaseInitialized() || !auth) {
    return { success: false, error: 'Firebase is not initialized.' };
  }

  try {
    const result: UserCredential | null = await getRedirectResult(auth);
    if (!result) {
      return { success: false };
    }
    const idToken = await result.user.getIdToken();
    return {
      success: true,
      user: result.user,
      idToken,
      accessToken: undefined,
    };
  } catch (error) {
    console.error('Microsoft SAML redirect handling error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to complete sign-in',
    };
  }
};

/**
 * Microsoft SSO via Firebase SAML (GCIP).
 * Uses redirect in Office so the task pane is not blocked by popup restrictions.
 */
export const signInWithMicrosoft = async (): Promise<FirebaseAuthResult> => {
  if (!isFirebaseInitialized() || !auth) {
    return {
      success: false,
      error: 'Firebase is not initialized. Please check your configuration.',
    };
  }

  const samlProvider = getSamlProvider();
  if (!samlProvider) {
    return {
      success: false,
      error:
        'Microsoft SSO is not configured. Set FIREBASE_SAML_PROVIDER_ID (same value as dashboard VITE_FIREBASE_SAML_PROVIDER_ID).',
    };
  }

  if (useMicrosoftSamlRedirectFlow()) {
    try {
      await signInWithRedirect(auth, samlProvider);
      return { success: true, redirectInitiated: true };
    } catch (error) {
      console.error('Microsoft SAML redirect start error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to start Microsoft sign-in',
      };
    }
  }

  try {
    const result: UserCredential = await signInWithPopup(auth, samlProvider);
    const idToken = await result.user.getIdToken();
    return {
      success: true,
      user: result.user,
      idToken,
      accessToken: undefined,
    };
  } catch (error) {
    console.error('Microsoft SAML authentication error:', error);

    let errorMessage = 'Authentication failed';
    if (error instanceof Error) {
      if (error.message.includes('auth/popup-closed-by-user')) {
        errorMessage = 'Sign-in was cancelled';
      } else if (error.message.includes('auth/popup-blocked')) {
        errorMessage = 'Popup was blocked — allow popups for this add-in and try again';
      } else {
        errorMessage = error.message;
      }
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Sign in with email and password using Firebase Auth
 */
export const signInWithFirebaseEmail = async (
  email: string,
  password: string
): Promise<FirebaseAuthResult> => {
  if (!isFirebaseInitialized() || !auth) {
    return {
      success: false,
      error: 'Firebase is not initialized. Please check your configuration.',
    };
  }

  try {
    console.log('🔥 Starting Firebase email/password authentication...');
    console.log('📧 Email:', email);

    const result: UserCredential = await signInWithEmailAndPassword(auth, email, password);

    console.log('✅ Firebase email/password authentication successful');
    console.log('👤 User email:', result.user.email);
    console.log('👤 User UID:', result.user.uid);
    console.log('👤 Email verified:', result.user.emailVerified);

    // Get Firebase ID token
    const idToken = await result.user.getIdToken();
    console.log('🔑 Firebase ID token obtained:', idToken ? `${idToken.substring(0, 20)}...` : 'null');

    return {
      success: true,
      user: result.user,
      idToken: idToken,
    };
  } catch (error) {
    console.error('❌ Firebase email/password authentication error:', error);

    let errorMessage = 'Authentication failed';
    if (error instanceof Error) {
      if (error.message.includes('auth/user-not-found')) {
        errorMessage = 'No account found with this email address';
      } else if (error.message.includes('auth/wrong-password')) {
        errorMessage = 'Incorrect password';
      } else if (error.message.includes('auth/invalid-email')) {
        errorMessage = 'Invalid email address';
      } else if (error.message.includes('auth/user-disabled')) {
        errorMessage = 'This account has been disabled';
      } else if (error.message.includes('auth/invalid-credential')) {
        errorMessage = 'Invalid email or password';
      } else {
        errorMessage = error.message;
      }
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Get the current Firebase user
 */
export const getCurrentFirebaseUser = (): User | null => {
  if (!auth) {
    return null;
  }
  return auth.currentUser;
};

/**
 * Sign out from Firebase Auth
 */
export const signOut = async (): Promise<void> => {
  if (!auth) {
    console.warn('⚠️ Firebase not initialized - cannot sign out');
    return;
  }

  try {
    await auth.signOut();
    console.log('✅ Signed out from Firebase successfully');
  } catch (error) {
    console.error('❌ Firebase sign out error:', error);
    throw error;
  }
};

/**
 * Get the Firebase ID token for backend authentication
 */
export const getFirebaseIdToken = async (): Promise<string | null> => {
  if (!auth) {
    console.error('❌ Firebase not initialized');
    return null;
  }

  try {
    const user = auth.currentUser;
    if (!user) {
      console.log('❌ No authenticated Firebase user found');
      return null;
    }

    const token = await user.getIdToken();
    console.log('🔑 Firebase ID token obtained');
    return token;
  } catch (error) {
    console.error('❌ Error getting Firebase ID token:', error);
    return null;
  }
};

/**
 * Extract user information from Firebase User object
 */
export const extractFirebaseUserInfo = (user: User): FirebaseUserInfo => {
  return {
    email: user.email || '',
    name: user.displayName || user.email?.split('@')[0] || '',
    displayName: user.displayName || user.email?.split('@')[0] || '',
    uid: user.uid,
    emailVerified: user.emailVerified,
  };
};

/**
 * Validate Firebase ID token format
 */
export const validateFirebaseIdToken = (token: string): boolean => {
  try {
    // Firebase ID tokens are JWT tokens with 3 parts separated by dots
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('❌ Invalid Firebase ID token format - not a JWT');
      return false;
    }

    // Decode the header to check if it's a Firebase token
    const header = JSON.parse(atob(parts[0]));
    if (header.alg !== 'RS256') {
      console.error('❌ Invalid Firebase ID token - wrong algorithm');
      return false;
    }

    console.log('✅ Firebase ID token format is valid');
    return true;
  } catch (error) {
    console.error('❌ Error validating Firebase ID token:', error);
    return false;
  }
};
